import { withApiRequestScope } from "../lib/api";
import { HierarchyAccessError, id, object, request, session, type HierarchyScope, type HierarchyThread } from "./api";

export interface AccountHistoryPage {
  scope: HierarchyScope;
  threads: HierarchyThread[];
  nextCursor?: string;
}

export async function authorizeAccountHistory(accountId: string, signal?: AbortSignal, expected?: HierarchyScope): Promise<HierarchyScope> {
  id(accountId);
  const scope = await session(signal);
  if (scope.accountId !== accountId || (expected && (scope.userId !== expected.userId || scope.accountId !== expected.accountId)) ||
    (scope.role !== "owner" && scope.role !== "manager")) throw new HierarchyAccessError();
  return scope;
}

export function accountHistoryThread(row: Record<string, unknown>, accountId: string, threadId?: string): HierarchyThread {
  if (row.accountId !== accountId || (threadId !== undefined && row.id !== threadId) || row.workspaceId !== "" ||
    (row.projectId !== undefined && row.projectId !== "") || row.role !== "manager" || typeof row.name !== "string" ||
    [row.forkedFromNodeId, row.forkedFromThreadId].some((value) => value !== undefined && value !== "")) throw new HierarchyAccessError();
  // Deliberately discard accessRole: an account membership is not a workspace
  // mutation grant. These account-level records are history-only.
  return { id: id(row.id), accountId, workspaceId: "", name: row.name };
}

export async function loadAccountHistory(accountId: string, previous?: AccountHistoryPage, signal?: AbortSignal): Promise<AccountHistoryPage> {
  return withApiRequestScope(async (active) => {
    if (previous && (previous.scope.accountId !== accountId || !previous.nextCursor)) throw new HierarchyAccessError();
    const scope = await authorizeAccountHistory(accountId, active, previous?.scope);
    const after = previous?.nextCursor;
    const body = await request(`/code/account-threads${after ? `?after=${encodeURIComponent(id(after))}` : ""}`, active);
    if (body.accountId !== accountId || !Array.isArray(body.threads) || body.threads.length > 50) throw new HierarchyAccessError();
    const rows = body.threads.map((row) => accountHistoryThread(object(row), accountId));
    const nextCursor = body.nextCursor === undefined ? undefined : id(body.nextCursor);
    const threads = [...(previous?.threads ?? []), ...rows];
    if (new Set(threads.map((row) => row.id)).size !== threads.length ||
      (nextCursor !== undefined && (nextCursor !== rows.at(-1)?.id || nextCursor === after))) throw new HierarchyAccessError();
    await authorizeAccountHistory(accountId, active, scope);
    active.throwIfAborted();
    return { scope, threads, nextCursor };
  }, signal);
}
